/** Contratos de administracion: usuarios, conflictos, auditoria, reportes, descargas. */

import { z } from "zod";
import { roleSchema } from "./auth.js";
import {
  idSchema,
  instantSchema,
  MAX_REPORT_STUDENTS,
  moneySchema,
  operationalDateSchema,
} from "./common.js";
import { paymentMethodSchema, paymentStatusSchema } from "./payments.js";

// ---------------------------------------------------------------------------
// Usuarios
// ---------------------------------------------------------------------------

export const userSchema = z.object({
  id: idSchema,
  institutionId: idSchema,
  username: z.string(),
  fullName: z.string(),
  email: z.string().nullable(),
  roles: z.array(roleSchema),
  active: z.boolean(),
  lockedUntil: instantSchema.nullable(),
  failedAttempts: z.number().int(),
  lastLoginAt: instantSchema.nullable(),
  createdAt: instantSchema,
  version: z.number().int(),
});
export type UserDto = z.infer<typeof userSchema>;

export const createUserSchema = z.object({
  id: idSchema,
  username: z
    .string()
    .trim()
    .min(3)
    .max(64)
    .regex(
      /^[a-z0-9._-]+$/,
      "Solo minúsculas, números, punto, guion y guion bajo",
    ),
  fullName: z.string().trim().min(3).max(120),
  email: z.string().trim().email().max(160).nullable().default(null),
  password: z.string().min(10).max(200),
  roles: z.array(roleSchema).min(1),
});
export type CreateUserRequest = z.infer<typeof createUserSchema>;

/** Un Administrador puede cambiar el rol de un usuario existente, no solo al crearlo. */
export const updateUserSchema = z.object({
  fullName: z.string().trim().min(3).max(120).optional(),
  email: z.string().trim().email().max(160).nullable().optional(),
  roles: z.array(roleSchema).min(1).optional(),
  active: z.boolean().optional(),
  /** Version esperada para el control de concurrencia optimista. */
  expectedVersion: z.number().int().nonnegative(),
});
export type UpdateUserRequest = z.infer<typeof updateUserSchema>;

// ---------------------------------------------------------------------------
// Conflictos
// ---------------------------------------------------------------------------

export const conflictCauseSchema = z.enum([
  "VERSION_DESACTUALIZADA",
  "SALDO_INSUFICIENTE",
  "CARGO_YA_LIQUIDADO",
  "CONCEPTO_INACTIVO",
  "ESTUDIANTE_REASIGNADO",
  "ESTUDIANTE_INACTIVO",
  "CICLO_CERRADO",
  "USUARIO_REVOCADO",
  "DISPOSITIVO_REVOCADO",
  "IMPORTE_DUPLICADO_SOSPECHOSO",
  "CODIGO_ESTUDIANTE_DUPLICADO",
]);

export const resolutionActionSchema = z.enum([
  "ACEPTAR_LOCAL",
  "RECHAZAR_LOCAL",
  "REGISTRAR_CORREGIDO",
]);

export const conflictSchema = z.object({
  id: idSchema,
  operationId: idSchema,
  operationType: z.string(),
  cause: conflictCauseSchema,
  causeLabel: z.string(),
  status: z.enum(["ABIERTO", "RESUELTO", "DESCARTADO"]),
  /** Evidencia inmutable del dispositivo. */
  localSnapshot: z.unknown(),
  /** Estado del servidor al momento del rechazo. */
  serverSnapshot: z.unknown(),
  monetaryImpact: moneySchema.nullable(),
  studentName: z.string().nullable(),
  capturedByUserName: z.string().nullable(),
  detectedAt: instantSchema,
  resolvedAt: instantSchema.nullable(),
  resolvedByUserName: z.string().nullable(),
  resolutionAction: resolutionActionSchema.nullable(),
  resolutionNote: z.string().nullable(),
  availableActions: z.array(resolutionActionSchema),
  requiresReview: z.boolean(),
});
export type ConflictDto = z.infer<typeof conflictSchema>;

export const resolveConflictSchema = z.object({
  action: resolutionActionSchema,
  note: z.string().trim().max(500).nullable().default(null),
  /** Requerido solo para REGISTRAR_CORREGIDO: el pago corregido a registrar. */
  correctedPaymentId: idSchema.nullable().default(null),
});
export type ResolveConflictRequest = z.infer<typeof resolveConflictSchema>;

// ---------------------------------------------------------------------------
// Auditoria
// ---------------------------------------------------------------------------

export const auditEventSchema = z.object({
  id: idSchema,
  institutionId: idSchema,
  /** Instante UTC. La auditoria es append-only: no se edita ni se borra. */
  occurredAt: instantSchema,
  actorUserId: idSchema.nullable(),
  actorUserName: z.string().nullable(),
  installationId: idSchema.nullable(),
  action: z.string(),
  entityType: z.string(),
  entityId: z.string().nullable(),
  outcome: z.enum(["EXITO", "FALLO", "DENEGADO"]),
  /** Metadatos seguros: nunca secretos ni datos financieros sensibles. */
  metadata: z.record(z.unknown()).nullable(),
});
export type AuditEventDto = z.infer<typeof auditEventSchema>;

export const auditQuerySchema = z.object({
  action: z.string().max(64).optional(),
  entityType: z.string().max(64).optional(),
  entityId: z.string().max(64).optional(),
  actorUserId: idSchema.optional(),
  outcome: z.enum(["EXITO", "FALLO", "DENEGADO"]).optional(),
  from: instantSchema.optional(),
  to: instantSchema.optional(),
});

// ---------------------------------------------------------------------------
// Reportes
// ---------------------------------------------------------------------------

export const reportFiltersSchema = z.object({
  cycleId: idSchema.optional(),
  from: operationalDateSchema.optional(),
  to: operationalDateSchema.optional(),
  gradeId: idSchema.optional(),
  sectionId: idSchema.optional(),
  /**
   * Estudiantes concretos que entran en el reporte.
   *
   * Es una LISTA, no un solo estudiante. La institución necesita un mismo documento
   * para varios alumnos que no tienen por qué ser familia: hermanos con apellidos
   * distintos, un patrocinador que cubre a varios, un grupo becado. Con un único
   * identificador había que emitir un reporte por alumno y graparlos.
   *
   * En la URL viaja separado por comas (`studentIds=a,b,c`) porque el lector de la
   * cadena de consulta se queda con un solo valor por clave y descartaría los
   * parámetros repetidos. Desde código se acepta el arreglo tal cual.
   */
  studentIds: z
    .preprocess(
      (valor) =>
        typeof valor === "string"
          ? valor.split(",").filter((parte) => parte !== "")
          : valor,
      z.array(idSchema).max(MAX_REPORT_STUDENTS),
    )
    .optional(),
  conceptId: idSchema.optional(),
  method: paymentMethodSchema.optional(),
  /**
   * Número de boleta, cheque o transferencia.
   *
   * Es como se busca un pago que no fue en efectivo: el encargado llama diciendo
   * «deposité con la boleta 4512», no el código del alumno ni la fecha. Sin esto
   * había que recorrer el reporte entero a ojo.
   */
  reference: z.string().trim().min(1).max(40).optional(),
  userId: idSchema.optional(),
  paymentStatus: paymentStatusSchema.optional(),
  syncStatus: z
    .enum(["TODOS", "PENDIENTES", "RECHAZADOS", "CONFLICTO"])
    .default("TODOS"),
  /**
   * Fecha de emisión que se imprime en el encabezado del PDF y del Excel.
   *
   * NO altera ninguna cifra: los datos siguen siendo los del corte real, que se
   * imprime aparte. Sirve para emitir un reporte fechado el día en que se entrega,
   * que es como se archiva en la institución.
   */
  issuedAt: operationalDateSchema.optional(),
});
export type ReportFilters = z.infer<typeof reportFiltersSchema>;

export const reportKindSchema = z.enum([
  "PAGOS_POR_PERIODO",
  "PAGOS_POR_ESTUDIANTE",
  "PAGOS_POR_CONCEPTO",
  "PAGOS_POR_METODO",
  "SALDOS_PENDIENTES",
  "AL_DIA",
  "OPERACIONES_OFFLINE",
  "RESUMEN_SINCRONIZACION",
  "ANULACIONES_Y_AJUSTES",
  "MORA_ACUMULADA",
  "MORA_EXONERADA",
]);
export type ReportKind = z.infer<typeof reportKindSchema>;

export const REPORT_LABELS: Readonly<Record<ReportKind, string>> =
  Object.freeze({
    PAGOS_POR_PERIODO: "Pagos por período",
    PAGOS_POR_ESTUDIANTE: "Pagos por estudiante",
    PAGOS_POR_CONCEPTO: "Pagos por concepto",
    PAGOS_POR_METODO: "Pagos por método",
    SALDOS_PENDIENTES: "Saldos pendientes (capital y mora)",
    AL_DIA: "Estudiantes al día con sus pagos",
    MORA_ACUMULADA: "Mora acumulada por estudiante",
    MORA_EXONERADA: "Mora exonerada",
    OPERACIONES_OFFLINE:
      "Operaciones offline pendientes, rechazadas o en conflicto",
    RESUMEN_SINCRONIZACION: "Resumen de sincronización",
    ANULACIONES_Y_AJUSTES: "Anulaciones y ajustes",
  });

/**
 * Resultado tabular generico. Pantalla, PDF y Excel consumen EXACTAMENTE esta
 * estructura, calculada por una sola funcion en el servidor. Por eso las tres
 * salidas no pueden diferir en un centavo.
 */
export const reportResultSchema = z.object({
  kind: reportKindSchema,
  title: z.string(),
  columns: z.array(
    z.object({
      key: z.string(),
      label: z.string(),
      type: z.enum(["texto", "numero", "dinero", "fecha", "estado"]),
      align: z.enum(["izquierda", "derecha", "centro"]).default("izquierda"),
    }),
  ),
  rows: z.array(z.record(z.union([z.string(), z.number(), z.null()]))),
  totals: z.record(z.union([z.string(), z.number(), z.null()])).nullable(),
  filters: reportFiltersSchema,
  /** Fecha de corte del reporte. Se imprime en el PDF y el Excel. */
  asOf: instantSchema,
  /** `true` si el resultado incluye operaciones aun no sincronizadas. */
  includesUnsynced: z.boolean(),
  truncated: z.boolean(),
});
export type ReportResult = z.infer<typeof reportResultSchema>;

// ---------------------------------------------------------------------------
// Descargas ZIP y notificaciones
// ---------------------------------------------------------------------------

export const downloadJobSchema = z.object({
  id: idSchema,
  studentId: idSchema,
  studentName: z.string(),
  gradeId: idSchema,
  gradeName: z.string(),
  operationalDate: operationalDateSchema,
  number: z.number().int().positive(),
  fileName: z.string(),
  status: z.enum([
    "EN_COLA",
    "GENERANDO",
    "LISTO",
    "DESCARGADO",
    "FALLIDO",
    "CANCELADO",
  ]),
  requestedAt: instantSchema,
  finishedAt: instantSchema.nullable(),
  /** `true` si el archivo temporal sigue disponible para descargar. */
  available: z.boolean(),
  errorMessage: z.string().nullable(),
});
export type DownloadJobDto = z.infer<typeof downloadJobSchema>;

export const createDownloadSchema = z.object({
  jobId: idSchema,
  studentId: idSchema,
  /** Instante en que el usuario presiono «Descargar». Ordena la concurrencia. */
  requestedAt: instantSchema,
  /** Identificador unico de la solicitud; desempata determinsticamente. */
  clientRequestId: z.string().min(8).max(64),
});
export type CreateDownloadRequest = z.infer<typeof createDownloadSchema>;

export const notificationSchema = z.object({
  id: idSchema,
  kind: z.enum(["DESCARGA_LISTA", "DESCARGA_FALLIDA", "ARCHIVO_PENDIENTE"]),
  jobId: idSchema.nullable(),
  title: z.string(),
  body: z.string(),
  createdAt: instantSchema,
  readAt: instantSchema.nullable(),
  /** Solo para ARCHIVO_PENDIENTE: la unica accion ofrecida al Administrador. */
  actionLabel: z.string().nullable(),
});
export type NotificationDto = z.infer<typeof notificationSchema>;

// ---------------------------------------------------------------------------
// Configuracion
// ---------------------------------------------------------------------------

export const updateInstitutionSchema = z.object({
  name: z.string().trim().min(3).max(160).optional(),
  legalName: z.string().trim().max(160).nullable().optional(),
  taxId: z.string().trim().max(40).nullable().optional(),
  address: z.string().trim().max(300).nullable().optional(),
  phone: z.string().trim().max(40).nullable().optional(),
  timeZone: z.string().trim().max(64).optional(),
  allowOverpayment: z.boolean().optional(),
  expectedVersion: z.number().int().nonnegative(),
});

export const healthSchema = z.object({
  status: z.enum(["ok", "degradado"]),
  version: z.string(),
  contractVersion: z.string(),
  serverTime: instantSchema,
  database: z.enum(["ok", "error"]),
  storage: z.enum(["ok", "error"]),
});
export type HealthDto = z.infer<typeof healthSchema>;
